import { BarChart } from "@mui/x-charts/BarChart";
import { useDrawingArea, useXScale } from "@mui/x-charts";

// histogram prop shape:
// {
//   label: string,             // chart title shown above
//   values?: number[],         // raw data values for a single series; bins computed automatically
//   bins?: string[],           // x-axis category labels; omit to auto-compute from values
//   layers?: Array<{           // multiple series; use instead of values for multi-series charts
//     label: string,           // series name (shown in legend when >1 layer)
//     counts?: number[],       // bar heights, aligned to bins (required if bins provided)
//     values?: number[],       // raw data values; bins/counts computed automatically if bins omitted
//     color?: string,          // defaults to DEFAULT_LAYER_COLORS[i]
//   }>,
//   vlines?: Array<string | number>,  // optional vertical reference lines; each entry is a bin
//                                     // label string or a raw numeric value snapped to nearest bin
// }
//
// Example (single series, auto bins):
// {
//   label: "Wind Speed (m/s)",
//   values: [1.2, 3.4, 4.1, 5.5, ...],
//   vlines: [4, 8],
// }
//
// Example (multi-series, explicit bins):
// {
//   label: "Span Length Distribution",
//   bins: ["0-10", "10-20", "20-30"],
//   layers: [
//     { label: "Before", counts: [5, 12, 3] },
//     { label: "After",  counts: [2,  8, 7] },
//   ],
// }
//
const DEFAULT_LAYER_COLORS = ["#1976d2", "#e65100", "#2e7d32", "#6a1b9a"];

function buildHistogramBins(layers) {
    const allValues = layers.flatMap((l) => l.values ?? []);
    if (allValues.length === 0) return { bins: [], processedLayers: layers, binEdges: null, binWidth: null };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    if (min === max) {
        const bins = [String(min)];
        return {
            bins,
            processedLayers: layers.map((l) => ({ ...l, counts: l.values ? [l.values.length] : l.counts })),
            binEdges: [min - 0.5, min + 0.5],
            binWidth: 1,
        };
    }

    const targetBins = Math.max(2, Math.min(20, Math.ceil(Math.log2(allValues.length) + 1)));
    const rawWidth = (max - min) / targetBins;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawWidth)));
    const normalized = rawWidth / magnitude;
    const niceFactor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    const binWidth = niceFactor * magnitude;

    const start = Math.floor(min / binWidth) * binWidth;
    const binEdges = [];
    for (let e = start; e <= max + binWidth * 0.0001; e = Math.round((e + binWidth) * 1e10) / 1e10) {
        binEdges.push(e);
    }
    if (binEdges.length < 2) binEdges.push(binEdges[0] + binWidth);

    const fmt = (v) => (Number.isInteger(v) ? String(v) : String(parseFloat(v.toPrecision(3))));
    const bins = binEdges.slice(0, -1).map((lo, i) => `${fmt(lo)}-${fmt(binEdges[i + 1])}`);

    const processedLayers = layers.map((layer) => {
        if (!layer.values) return layer;
        const counts = new Array(bins.length).fill(0);
        for (const v of layer.values) {
            const idx = Math.max(0, Math.min(bins.length - 1, Math.floor((v - binEdges[0]) / binWidth)));
            counts[idx]++;
        }
        return { ...layer, counts };
    });

    return { bins, processedLayers, binEdges, binWidth };
}

function VLines({ vlines, bins, binEdges, binWidth }) {
    const xScale = useXScale();
    const { top, height } = useDrawingArea();

    return vlines.map((entry, i) => {
        const value = typeof entry === "object" && entry !== null ? entry.value : entry;
        const dashed = typeof entry === "object" && entry !== null ? (entry.dashed ?? false) : false;
        const color = typeof entry === "object" && entry !== null ? (entry.color ?? "#FF4560") : "#FF4560";

        const numVal = Number(value);
        let x;

        if (!isNaN(numVal) && binEdges && binWidth) {
            // Proportional position within the bin
            const binIdx = Math.max(0, Math.min(bins.length - 1, Math.floor((numVal - binEdges[0]) / binWidth)));
            const bandLeft = xScale(bins[binIdx]);
            if (bandLeft === undefined) return null;
            const fraction = Math.min(1, Math.max(0, (numVal - binEdges[binIdx]) / binWidth));
            x = bandLeft + fraction * xScale.bandwidth();
        } else {
            // Snap to center of matched bin label
            const binLabel = bins.includes(String(value)) ? String(value) : null;
            if (!binLabel) return null;
            const bandLeft = xScale(binLabel);
            if (bandLeft === undefined) return null;
            x = bandLeft + xScale.bandwidth() / 2;
        }

        return (
            <line
                key={i}
                x1={x} x2={x} y1={top} y2={top + height}
                stroke={color}
                strokeWidth={2}
                strokeDasharray={dashed ? "4 3" : undefined}
            />
        );
    });
}

export default function Histogram({ histogram }) {
    if (!histogram) return null;

    // Normalize to layers array
    const rawLayers = histogram.layers ?? [{ counts: histogram.counts, values: histogram.values }];
    const vlines = histogram.vlines ?? [];
    const vlineLegend = histogram.vline_legend ?? [];

    const needsDynamicBins = !histogram.bins && rawLayers.some((l) => l.values);
    const { bins, processedLayers, binEdges, binWidth } = needsDynamicBins
        ? buildHistogramBins(rawLayers)
        : { bins: histogram.bins, processedLayers: rawLayers, binEdges: null, binWidth: null };

    return (
        <div style={{ marginTop: "4px" }}>
            <div style={{ fontSize: "13px", color: "#666666", marginBottom: "2px" }}>{histogram.label}</div>
            <BarChart
                height={130}
                series={processedLayers.map((layer, i) => ({
                    data: layer.counts,
                    label: layer.label,
                    color: layer.color ?? DEFAULT_LAYER_COLORS[i % DEFAULT_LAYER_COLORS.length],
                }))}
                xAxis={[{ data: bins, scaleType: "band", tickLabelStyle: { fontSize: 11 }, label: histogram.x_label, labelStyle: { fontSize: 11 } }]}
                yAxis={[{ tickNumber: 3, tickLabelStyle: { fontSize: 11 }, label: histogram.y_label, labelStyle: { fontSize: 11 } }]}
                margin={{ top: 8, right: 8, bottom: histogram.x_label ? 36 : 24, left: histogram.y_label ? 52 : 32 }}
                slotProps={{ legend: { hidden: processedLayers.length <= 1 } }}
            >
                <VLines vlines={vlines} bins={bins} binEdges={binEdges} binWidth={binWidth} />
            </BarChart>
            {vlineLegend.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginTop: "2px", paddingLeft: histogram.y_label ? 52 : 32 }}>
                    {vlineLegend.map((item, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "11px", color: "#666666" }}>
                            <svg width="20" height="10" style={{ flexShrink: 0 }}>
                                {item.type === "bar"
                                    ? <rect x="0" y="1" width="20" height="8" fill={item.color} />
                                    : <line x1="0" y1="5" x2="20" y2="5" stroke={item.color} strokeWidth="2" strokeDasharray={item.dashed ? "4 3" : undefined} />
                                }
                            </svg>
                            {item.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
